import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";
import HeroSlider from "@/components/HeroSlider";

const IMAGES = {
  aura: "/images/aura/facade.jpg",
  auraInterior: "/images/aura/lobby.jpg",
  seafood: "/images/seafood/facade.jpg",
  inn: "/images/inn/exterior.jpg",
  bakery: "/images/stock/bakery_interior.jpg",
  jewellers: "/images/stock/jewellery_gold.jpg",
};

const HERO_SLIDES = [
  { src: IMAGES.seafood,   alt: "Amrut Seafood Restaurant" },
  { src: IMAGES.aura,      alt: "Amrut Aura — Premium Hotel" },
  { src: IMAGES.inn,       alt: "Amrut Inn — Comfort Stay" },
  { src: IMAGES.jewellers, alt: "Amrut Jewellers" },
  { src: IMAGES.bakery,    alt: "Amrut Delight" },
];

const TESTIMONIALS = [
  { quote: "We've been coming here for years. The rooms are always spotless, the food is always fresh, and the staff remembers your name. That's rare.", author: "Priya Menon", detail: "Returning Guest" },
  { quote: "The seafood here is something else. You can taste the difference when it's fresh and cooked without shortcuts. My family drives down from Goa just for the Dal Toy.", author: "Rahul Deshmukh", detail: "Food Lover" },
  { quote: "Clean rooms, honest pricing, and people who genuinely care. Amrut feels like a home away from home every single time.", author: "Ananya Sharma", detail: "Business Traveller" },
];

const PHILOSOPHY = [
  { title: "No Shortcuts", text: "From the kitchen to the rooms — everything is done the right way. Fresh ingredients, clean spaces, and honest service. That's how it's always been." },
  { title: "People First", text: "Our staff has been with us for years. They know the guests, they care about the details, and that shows in every interaction." },
  { title: "Rooted in Karwar", text: "This is where we started, and this is who we are. The food, the hospitality, the way we do things — it all comes from this place." },
  { title: "Repeat Guests, Not Just Customers", text: "Most of our guests come back. Some have been with us for decades. We think that says more than any award ever could." },
];

const TINT = "absolute inset-0 bg-gradient-to-t from-charcoal/95 via-charcoal/55 to-charcoal/40 pointer-events-none";

export default function HomePage() {
  return (
    <div data-testid="home-page" className="bg-ivory">
      {/* ─── HERO SLIDER ─── */}
      <HeroSlider slides={HERO_SLIDES}>
        <div className="flex flex-col items-center justify-center h-full text-center px-6">
          <AnimatedSection delay={0.2}>
            <p className="overline-text text-ivory/80 mb-6 tracking-[0.3em]">Karwar, Karnataka</p>
          </AnimatedSection>
          <AnimatedSection delay={0.4}>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-8xl text-ivory font-light tracking-tighter leading-none mb-6 drop-shadow-lg">Amrut Group</h1>
          </AnimatedSection>
          <AnimatedSection delay={0.6}>
            <p className="font-sans text-sm md:text-base text-ivory/85 max-w-md leading-relaxed mb-10">
              Serving Karwar since 1976. Hotels, restaurant, bakery, and jewellery — all under one roof.
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.8}>
            <Link to="/about" data-testid="hero-explore-btn" className="inline-flex items-center gap-3 px-8 py-3 border border-ivory/40 text-ivory text-xs tracking-[0.2em] uppercase font-medium hover:bg-ivory/10 transition-all duration-500 backdrop-blur-sm">
              Explore <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </AnimatedSection>
        </div>
      </HeroSlider>

      {/* ─── BRAND INTRODUCTION ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[900px] mx-auto text-center">
          <AnimatedSection><div className="gold-divider mx-auto mb-10" /></AnimatedSection>
          <AnimatedSection delay={0.1}>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal font-light tracking-tight leading-tight mb-8">
              A family of businesses,<br className="hidden md:block" /> built on trust
            </h2>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <p className="text-base md:text-lg text-charcoal/60 leading-relaxed max-w-2xl mx-auto">
              What started as a restaurant in 1976 has grown into something much bigger.
              Today, Amrut Group runs a premium hotel, a 500-seater seafood restaurant
              featured on History Channel and NDTV, a budget-friendly inn, a bakery with
              two outlets, and a trusted jewellery store. All in Karwar, all run by the same family.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── EXPERIENCE BLOCKS ─── */}
      <section data-testid="experience-blocks" className="pb-8 bg-ivory">
        <Link to="/amrut-seafood" data-testid="experience-seafood-block" className="block group">
          <div className="relative w-full h-[70vh] md:h-[80vh] overflow-hidden">
            <img src={IMAGES.seafood} alt="Amrut Seafood Restaurant" className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
            <div className={TINT} />
            <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-16 lg:p-24">
              <AnimatedSection>
                <p className="overline-text text-ivory/70 mb-4">Since 1976</p>
                <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ivory font-light tracking-tight mb-4 drop-shadow-lg">Amrut Seafood</h3>
                <p className="text-sm text-ivory/85 max-w-md leading-relaxed mb-6">500 seats, locally sourced fish, no additives. Featured on History Channel and NDTV Good Times.</p>
                <span className="inline-flex items-center gap-2 text-gold text-xs tracking-[0.2em] uppercase font-medium group-hover:gap-4 transition-all duration-500">Explore Menu <ArrowRight size={14} strokeWidth={1.5} /></span>
              </AnimatedSection>
            </div>
          </div>
        </Link>

        <Link to="/amrut-aura" data-testid="experience-aura-block" className="block group mt-2">
          <div className="relative w-full h-[70vh] md:h-[80vh] overflow-hidden">
            <img src={IMAGES.aura} alt="Amrut Aura premium hotel" className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
            <div className={TINT} />
            <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-16 lg:p-24">
              <AnimatedSection>
                <p className="overline-text text-ivory/70 mb-4">Premium Hotel</p>
                <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ivory font-light tracking-tight mb-4 drop-shadow-lg">Amrut Aura</h3>
                <p className="text-sm text-ivory/85 max-w-md leading-relaxed mb-6">Fully air-conditioned rooms, mountain views, and the kind of sleep quality people keep coming back for.</p>
                <span className="inline-flex items-center gap-2 text-gold text-xs tracking-[0.2em] uppercase font-medium group-hover:gap-4 transition-all duration-500">Discover <ArrowRight size={14} strokeWidth={1.5} /></span>
              </AnimatedSection>
            </div>
          </div>
        </Link>

        <Link to="/amrut-inn" data-testid="experience-inn-block" className="block group mt-2">
          <div className="relative w-full h-[70vh] md:h-[80vh] overflow-hidden">
            <img src={IMAGES.inn} alt="Amrut Inn" className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
            <div className={TINT} />
            <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-16 lg:p-24">
              <AnimatedSection>
                <p className="overline-text text-ivory/70 mb-4">Comfort Stay</p>
                <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ivory font-light tracking-tight mb-4 drop-shadow-lg">Amrut Inn</h3>
                <p className="text-sm text-ivory/85 max-w-md leading-relaxed mb-6">32 clean rooms, a vegetarian restaurant, and honest pricing. For families and travellers who want value without compromise.</p>
                <span className="inline-flex items-center gap-2 text-gold text-xs tracking-[0.2em] uppercase font-medium group-hover:gap-4 transition-all duration-500">Learn More <ArrowRight size={14} strokeWidth={1.5} /></span>
              </AnimatedSection>
            </div>
          </div>
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
          <Link to="/amrut-jewellers" data-testid="experience-jewellers-block" className="block group">
            <div className="relative w-full h-[55vh] md:h-[65vh] overflow-hidden">
              <img src={IMAGES.jewellers} alt="Amrut Jewellers" className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
              <div className={TINT} />
              <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-12">
                <AnimatedSection>
                  <p className="overline-text text-gold/80 mb-3">Since 1999</p>
                  <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl text-ivory font-light tracking-tight mb-3 drop-shadow-lg">Amrut Jewellers</h3>
                  <p className="text-sm text-ivory/85 max-w-sm leading-relaxed mb-5">100% hallmarked gold, certified diamonds, and handmade temple jewellery. Trusted for over two decades.</p>
                  <span className="inline-flex items-center gap-2 text-gold text-xs tracking-[0.2em] uppercase font-medium group-hover:gap-4 transition-all duration-500">Explore <ArrowRight size={14} strokeWidth={1.5} /></span>
                </AnimatedSection>
              </div>
            </div>
          </Link>
          <Link to="/amrut-delight" data-testid="experience-bakery-block" className="block group">
            <div className="relative w-full h-[55vh] md:h-[65vh] overflow-hidden">
              <img src={IMAGES.bakery} alt="Amrut Delight" className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
              <div className={TINT} />
              <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-12">
                <AnimatedSection>
                  <p className="overline-text text-ivory/70 mb-3">Artisan Bakery</p>
                  <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl text-ivory font-light tracking-tight mb-3 drop-shadow-lg">Amrut Delight</h3>
                  <p className="text-sm text-ivory/85 max-w-sm leading-relaxed mb-5">Handmade cakes, fresh patisserie, and two outlets in Karwar. All baked in-house.</p>
                  <span className="inline-flex items-center gap-2 text-gold text-xs tracking-[0.2em] uppercase font-medium group-hover:gap-4 transition-all duration-500">Discover <ArrowRight size={14} strokeWidth={1.5} /></span>
                </AnimatedSection>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* ─── PHILOSOPHY (now light) ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection>
            <p className="overline-text text-charcoal/50 mb-6">How We Work</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal font-light tracking-tight mb-20">What Makes Us Different</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-16">
            {PHILOSOPHY.map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="border-t border-charcoal/15 pt-8">
                  <h4 className="font-serif text-xl md:text-2xl text-charcoal font-light tracking-tight mb-4">{item.title}</h4>
                  <p className="text-sm text-charcoal/60 leading-relaxed">{item.text}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section data-testid="testimonials-section" className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection>
            <p className="overline-text text-charcoal/40 mb-6">What Guests Say</p>
            <div className="gold-divider mb-20" />
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            {TESTIMONIALS.map((t, i) => (
              <AnimatedSection key={i} delay={i * 0.15}>
                <blockquote className="border-t border-charcoal/10 pt-8">
                  <p className="font-serif text-lg md:text-xl text-charcoal/80 font-light leading-relaxed italic mb-8">"{t.quote}"</p>
                  <footer>
                    <p className="text-sm font-medium text-charcoal">{t.author}</p>
                    <p className="text-xs text-charcoal/40 mt-1">{t.detail}</p>
                  </footer>
                </blockquote>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-10 md:py-14 px-6 md:px-10 lg:px-16 bg-ivory text-center">
        <div className="max-w-[700px] mx-auto">
          <AnimatedSection><h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal font-light tracking-tight mb-6">Come Visit Us</h2></AnimatedSection>
          <AnimatedSection delay={0.1}><p className="text-sm md:text-base text-charcoal/50 leading-relaxed mb-10 max-w-md mx-auto">Whether you need a room, a meal, fresh cake, or a gold necklace — we're here in Karwar, and we'd love to see you.</p></AnimatedSection>
          <AnimatedSection delay={0.2}>
            <a href="https://wa.me/919845201215?text=Hello%20Amrut%20Group" target="_blank" rel="noopener noreferrer" data-testid="home-final-cta" className="inline-flex items-center gap-3 px-10 py-4 border border-charcoal/20 text-charcoal text-xs tracking-[0.2em] uppercase font-medium hover:bg-charcoal hover:text-ivory transition-all duration-500">
              Get in Touch <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
